// GENERATED FILE -- do not edit by hand.
// Regenerate with `npm run codegen:stores` (see frontend/package.json).
// Source: agent.app.project.Project

import { makeObservable, observable } from "mobx";
import { applyJsonPatch, JsonPatchOp } from "@/app_store/rpc/json_patch";
import { AgentTransport, callRpc } from "@/app_store/rpc/rpc";
import { registerEntity, wrapEntity } from "@/app_store/rpc/entity";
import { AgentSessionData, AgentSessionStore } from "./AgentSession.generated";
import { DeploymentContextData, DeploymentContextStore } from "./DeploymentContext.generated";
import { VaultData, VaultStore } from "./Vault.generated";
import { SandboxData, SandboxStore } from "./Sandbox.generated";

export interface ProjectData {
    // Stamped onto every @observable model's dump at runtime (see
    // common._ObservableMixin._serialize_with_tag) -- tells wrapEntity which
    // generated Store class this value belongs to. Never sent by a client.
    _entity_class_name: "Project";
    path: string | null;
    managed_repo_url: string | null;
    agent_sessions: AgentSessionData[];
    deployment: DeploymentContextData;
    vaults: Record<string, VaultData>;
    vault_sandbox: SandboxData;
}

export class ProjectStore {
    readonly _entity_class_name = "Project" as const;
    path: string | null;
    managed_repo_url: string | null;
    agent_sessions: AgentSessionStore[];
    deployment: DeploymentContextStore;
    vaults: Record<string, VaultStore>;
    vault_sandbox: SandboxStore;
    private _transport: AgentTransport;
    private _chainPath: string[];

    constructor(data: ProjectData, transport: AgentTransport, chainPath: string[] = []) {
        this._transport = transport;
        this._chainPath = chainPath;
        this.path = wrapEntity(data.path, transport, [...chainPath, "path"]) ?? null;
        this.managed_repo_url = wrapEntity(data.managed_repo_url, transport, [...chainPath, "managed_repo_url"]) ?? null;
        this.agent_sessions = wrapEntity(data.agent_sessions, transport, [...chainPath, "agent_sessions"]);
        this.deployment = wrapEntity(data.deployment, transport, [...chainPath, "deployment"]);
        this.vaults = Object.fromEntries(
            Object.entries(data.vaults ?? {}).map(([key, val]) => [
                key, wrapEntity(val, transport, [...chainPath, "vaults", key]),
            ])
        ) as Record<string, VaultStore>;
        this.vault_sandbox = wrapEntity(data.vault_sandbox, transport, [...chainPath, "vault_sandbox"]);
        makeObservable(this, {
            path: observable,
            managed_repo_url: observable,
            agent_sessions: observable,
            deployment: observable,
            vaults: observable,
            vault_sandbox: observable,
        });
    }

    applyPatch(op: JsonPatchOp): void {
        applyJsonPatch(this, op);
    }

    async get_container(): Promise<any> {
        return callRpc(this._transport, this._chainPath, "get_container", [], {});
    }

    async create_agent_session(backend?: string, id?: string): Promise<any> {
        return callRpc(this._transport, this._chainPath, "create_agent_session", [backend, id], {});
    }

    async destroy_agent_session(session_id: string): Promise<any> {
        return callRpc(this._transport, this._chainPath, "destroy_agent_session", [session_id], {});
    }

    async add_vault(name: string, engine: string): Promise<any> {
        return callRpc(this._transport, this._chainPath, "add_vault", [name, engine], {});
    }

    async remove_vault(vault_id: string): Promise<any> {
        return callRpc(this._transport, this._chainPath, "remove_vault", [vault_id], {});
    }

    async restore_sessions(): Promise<any> {
        return callRpc(this._transport, this._chainPath, "restore_sessions", [], {});
    }

    async restore_vaults(): Promise<any> {
        return callRpc(this._transport, this._chainPath, "restore_vaults", [], {});
    }
}

registerEntity("Project", ProjectStore);
